import { Boundaries } from "@planzone/core";
import { account, authenticationError } from "@planzone/dto";
import { Event, events } from "@planzone/events";
import { reduceResource } from "../../common/resource";
import { createStateReducer } from "../../utils/createStateReducer";
import { State, Selector } from "./types";
import { createSelector } from "./selector";

const initialState: State = {
  error: authenticationError,
  hasSession: false,
  request: {
    isPending: false,
    isSuccessful: false,
    isFailure: false,
  },
  account,
};

function handleRequest(state: State): State {
  return {
    ...state,
    request: { ...initialState.request, isPending: true },
  };
}

function handleSuccess(
  state: State,
  account: Boundaries["authenticate"]["success"],
): State {
  return {
    ...state,
    request: { ...initialState.request, isSuccessful: true },
    hasSession: true,
    error: initialState.error,
    account,
  };
}

function handleFailure(
  state: State,
  error: Boundaries["authenticate"]["failure"],
): State {
  return {
    ...state,
    request: { ...initialState.request, isFailure: true },
    error,
  };
}

function handlePreferencesEdition(
  state: State,
  { preferences }: Boundaries["editUserPreferences"]["success"],
): State {
  return { ...state, account: { ...state.account, preferences } };
}

function handleResourceEdition(
  state: State,
  resource: Boundaries["editResource"]["success"],
): State {
  return {
    ...state,
    account: {
      ...state.account,
      resource:
        state.account.uuid === resource.uuid
          ? resource
          : state.account.resource,
    },
  };
}

function handleChangePlanzone(
  state: State,
  { resource }: Boundaries["changePlanzone"]["success"],
): State {
  return {
    ...state,
    account: { ...state.account, resource },
  };
}

function handleValidatePersonalTimeRecordsUpToDate(
  state: State,
  {
    resource,
    date,
  }: Boundaries["validatePersonalTimeRecordsUpToDate"]["success"],
): State {
  return resource.uuid === state.account.resource.uuid
    ? {
        ...state,
        account: {
          ...state.account,
          resource: { ...state.account.resource, integratedAt: date },
        },
      }
    : state;
}

function handleLogOut(): State {
  return initialState;
}

function propagateEvent(state = initialState, event: Event): State {
  return {
    ...state,
    account: {
      ...state.account,
      resource: reduceResource(state.account.resource, event),
    },
  };
}

const reduceState = createStateReducer(
  initialState,
  {
    [events.authenticateRequested]: handleRequest,
    [events.authenticateSucceeded]: handleSuccess,
    [events.authenticateFailed]: handleFailure,
    [events.editUserPreferencesSucceeded]: handlePreferencesEdition,
    [events.editResourceSucceeded]: handleResourceEdition,
    [events.logOutSucceeded]: handleLogOut,
    [events.changePlanzoneSucceeded]: handleChangePlanzone,
    [events.validatePersonalTimeRecordsUpToDateSucceeded]:
      handleValidatePersonalTimeRecordsUpToDate,
  },
  propagateEvent,
);

export type { State, Selector };

export { reduceState, initialState, createSelector };
